/* eslint react/jsx-key: 0 */
import React, { ReactElement, PropsWithChildren, ReactNode } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable as DraggableRow,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import {
  useTable,
  TableOptions,
  useFlexLayout,
  useResizeColumns,
  PluginHook,
  useRowSelect,
} from 'react-table';
import classnames from 'classnames';
import { ResizeHandle } from './components/ResizeHandle';
import { DragHandle } from './components/DragHandle';
import styles from './components/Table/index.module.css';
import { DraggableContainer } from './components/DraggableContainer';

export interface TableDraggableRowsProps<T extends Record<string, unknown>>
  extends TableOptions<T> {
  actions?: (data: T) => ReactNode;
  ariaLabel?: string;
  className?: string;
  handleOnRowDrag: (result: DropResult) => void;
  pluginHooks?: PluginHook<T>[];
  rowClassName?: string;
}

const hooks = [useResizeColumns, useFlexLayout, useRowSelect];

export function TableDraggableRows<T extends Record<string, unknown>>({
  actions,
  ariaLabel = '',
  className,
  columns,
  data,
  handleOnRowDrag,
  pluginHooks = [],
  rowClassName,
}: PropsWithChildren<TableDraggableRowsProps<T>>): ReactElement {
  const tableHooks = [...hooks, ...pluginHooks];

  const { headerGroups, getTableBodyProps, getTableProps, prepareRow, rows } =
    useTable<T>(
      {
        columns,
        data,
        defaultColumn: {
          maxWidth: 300,
          minWidth: 20,
        },
      },
      ...tableHooks,
    );

  const rowClasses = classnames(styles.row, rowClassName, {
    [styles.hasActions]: !!actions,
  });

  return (
    <>
      <div
        className={classnames('table', styles.table, className)}
        aria-label={ariaLabel}
        {...getTableProps()}
      >
        <div className={styles.head}>
          <div className={rowClasses}>
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <div {...column.getHeaderProps()} className={styles.heading}>
                    {column.render('Header')}
                    {column.canResize && <ResizeHandle column={column} />}
                  </div>
                ))}
                {actions && <div className={styles.emptyHead}>&nbsp;</div>}
                {<div className={styles.emptyDragHandleHead}>&nbsp;</div>}
              </div>
            ))}
          </div>
        </div>
        <DragDropContext onDragEnd={handleOnRowDrag}>
          <Droppable droppableId="droppable">
            {(droppableProvided: DroppableProvided) => {
              return (
                <>
                  <div
                    {...droppableProvided.droppableProps}
                    {...getTableBodyProps()}
                    ref={droppableProvided.innerRef}
                    role="row"
                  >
                    {rows.map((row, index) => {
                      prepareRow(row);

                      return (
                        <DraggableRow
                          draggableId={row.id}
                          index={index}
                          key={row.index}
                        >
                          {(
                            draggableProvided: DraggableProvided,
                            snapshot: DraggableStateSnapshot,
                          ) => (
                            <DraggableContainer
                              className={rowClasses}
                              key={index}
                              innerRef={draggableProvided.innerRef}
                              isDragging={snapshot.isDragging}
                              {...{
                                ...draggableProvided.draggableProps,
                                style: {
                                  ...draggableProvided.draggableProps.style,
                                  ...row.getRowProps().style,
                                },
                              }}
                            >
                              {row.cells.map((cell) => {
                                return (
                                  <div
                                    {...cell.getCellProps()}
                                    className={styles.col}
                                    title={
                                      typeof cell.value === 'string'
                                        ? cell.value
                                        : undefined
                                    }
                                  >
                                    {cell.render('Cell')}
                                  </div>
                                );
                              })}
                              {actions && (
                                <div
                                  className={styles.actions}
                                  data-test="actions"
                                >
                                  {actions(row.original)}
                                </div>
                              )}

                              <DragHandle
                                {...draggableProvided.dragHandleProps}
                              />
                            </DraggableContainer>
                          )}
                        </DraggableRow>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </div>
                </>
              );
            }}
          </Droppable>
        </DragDropContext>
      </div>
    </>
  );
}
